import React, { useRef, useEffect, useState } from 'react';
import { SkuScanner } from './components/SkuScanner';
import { ProductResult } from './components/ProductResult';
import { useProductLookup } from './hooks/useProductLookup';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function App() {
  const { data, loading, error, lookup, reset } = useProductLookup();
  const scannerRef = useRef<HTMLDivElement>(null);
  const [currency, setCurrency] = useState<string>('USD');

  useEffect(() => {
    fetch(`${API_URL}/api/config`)
      .then((r) => r.json())
      .then((cfg) => { if (cfg?.currency) setCurrency(cfg.currency); })
      .catch(() => {}); // silently keep USD fallback
  }, []);

  // After each completed search (success or error), re-focus the input
  useEffect(() => {
    if (!loading) {
      const input = scannerRef.current?.querySelector('input');
      if (input) {
        // Small delay to let React finish rendering
        setTimeout(() => input.focus(), 100);
      }
    }
  }, [loading, data, error]);

  const handleSubmit = async (sku: string) => {
    await lookup(sku);
  };

  const handleClear = () => {
    reset();
  };

  return (
    <div style={appStyles.root}>
      {/* Header */}
      <header style={appStyles.header}>
        <div style={appStyles.headerInner}>
          <span style={appStyles.logo}>lokal</span>
          <span style={appStyles.tagline}>In-Store Inventory Lookup</span>
        </div>
      </header>

      {/* Main content */}
      <main style={appStyles.main}>
        {/* Scanner input */}
        <div ref={scannerRef} style={appStyles.scannerWrapper}>
          <SkuScanner
            onSubmit={handleSubmit}
            onClear={handleClear}
            loading={loading}
          />
        </div>

        {/* Loading state */}
        {loading && (
          <div style={appStyles.statusBox}>
            <div style={appStyles.loadingSpinner} />
            <span style={appStyles.loadingText}>Looking up SKU...</span>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div style={appStyles.errorBox}>
            <span style={appStyles.errorIcon}>&#x2715;</span>
            <span style={appStyles.errorText}>{error}</span>
          </div>
        )}

        {/* Results */}
        {!loading && data && (
          <div style={appStyles.resultsWrapper}>
            <ProductResult result={data} currency={currency} onVariantSelect={lookup} />
          </div>
        )}

        {/* Idle hint */}
        {!loading && !data && !error && (
          <div style={appStyles.hint}>
            <div style={appStyles.hintIcon}>&#x1F4F7;</div>
            <p style={appStyles.hintText}>
              Point your barcode scanner at any product tag, or type a SKU above.
            </p>
          </div>
        )}
      </main>

      {/* CSS keyframe for spinner — injected as a style tag */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { box-sizing: border-box; }
        input:focus {
          border-color: #3182ce !important;
          box-shadow: 0 0 0 3px rgba(49,130,206,0.25) !important;
        }
        button:hover:not(:disabled) {
          filter: brightness(1.1);
        }
        button:active:not(:disabled) {
          transform: scale(0.97);
        }
        [aria-label="Scan with camera"]:hover:not(:disabled) {
          border-color: #3182ce !important;
          color: #3182ce !important;
        }
      `}</style>
    </div>
  );
}

const appStyles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    background: '#0f0f0f',
    color: '#f7fafc',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#111',
    borderBottom: '1px solid #1a1a1a',
    padding: '0 24px',
  },
  headerInner: {
    maxWidth: '960px',
    margin: '0 auto',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  logo: {
    fontSize: '26px',
    fontWeight: 900,
    color: '#f7fafc',
    letterSpacing: '-0.03em',
    textTransform: 'lowercase',
  },
  tagline: {
    fontSize: '14px',
    color: '#4a5568',
    fontWeight: 500,
    borderLeft: '1px solid #2d3748',
    paddingLeft: '16px',
    marginLeft: '4px',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 0 64px',
    gap: '32px',
    width: '100%',
  },
  scannerWrapper: {
    width: '100%',
    maxWidth: '720px',
    padding: '0 16px',
  },
  statusBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 28px',
    background: '#1a1a1a',
    borderRadius: '12px',
    border: '1px solid #2d3748',
    maxWidth: '720px',
    width: 'calc(100% - 32px)',
  },
  loadingSpinner: {
    width: '24px',
    height: '24px',
    border: '3px solid #2d3748',
    borderTopColor: '#3182ce',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    flexShrink: 0,
  },
  loadingText: {
    fontSize: '18px',
    color: '#a0aec0',
    fontWeight: 500,
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '20px 28px',
    background: 'rgba(116,42,42,0.25)',
    border: '1.5px solid #742a2a',
    borderRadius: '12px',
    maxWidth: '720px',
    width: 'calc(100% - 32px)',
  },
  errorIcon: {
    fontSize: '22px',
    color: '#fc8181',
    flexShrink: 0,
  },
  errorText: {
    fontSize: '18px',
    color: '#fc8181',
    fontWeight: 500,
  },
  resultsWrapper: {
    width: '100%',
    maxWidth: '960px',
    padding: '0',
    animation: 'fadeIn 0.2s ease',
  },
  hint: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    marginTop: '48px',
    opacity: 0.35,
  },
  hintIcon: {
    fontSize: '56px',
  },
  hintText: {
    fontSize: '18px',
    color: '#a0aec0',
    textAlign: 'center',
    maxWidth: '400px',
    margin: 0,
    lineHeight: 1.5,
  },
};
